import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchCockpitNarrative, fetchCockpitNarrativeList } from '../api/cockpit';
import { ErrorState } from '../components/ui';
import { usePageTitle } from '../hooks/usePageTitle';

function renderMarkdown(markdown: string): string[] {
  return markdown.split(/\n{2,}/).map((section) => section.trim()).filter(Boolean);
}

export function Narratives() {
  usePageTitle('Narratives');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['cockpit', 'narratives'],
    queryFn: fetchCockpitNarrativeList,
    staleTime: 60_000,
  });

  const activeDate = selectedDate || listQuery.data?.[0]?.date || 'latest';
  const detailQuery = useQuery({
    queryKey: ['cockpit', 'narratives', activeDate],
    queryFn: () => fetchCockpitNarrative(activeDate),
    enabled: !!activeDate,
    staleTime: 60_000,
  });

  if (listQuery.isLoading) {
    return <div className="text-swiss-gray-500">Loading narratives...</div>;
  }

  if (listQuery.error) {
    return (
      <ErrorState
        title="Narratives Error"
        message={listQuery.error instanceof Error ? listQuery.error.message : 'Failed to load narratives'}
        onRetry={() => void listQuery.refetch()}
      />
    );
  }

  const entries = listQuery.data || [];
  const detail = detailQuery.data;

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr]">
      <aside>
        <h1 className="text-[24px] font-semibold tracking-tight leading-[1.2]">Narratives</h1>
        <p className="mt-2 text-[14px] text-swiss-gray-500">
          Daily operating narrative, newest first.
        </p>

        <div className="mt-6 border border-swiss-gray-200">
          {entries.map((entry) => {
            const isActive = entry.date === (detail?.date || activeDate);
            return (
              <button
                key={entry.date}
                onClick={() => setSelectedDate(entry.date)}
                className={`block w-full border-b border-swiss-gray-100 px-4 py-3 text-left transition-colors duration-150 ${
                  isActive ? 'bg-swiss-off-white text-swiss-black' : 'text-swiss-gray-500 hover:text-swiss-black'
                }`}
              >
                <div className="text-[13px] font-medium">{entry.date}</div>
                <div className="mt-1 text-[11px] uppercase tracking-widest">{entry.title}</div>
              </button>
            );
          })}
        </div>
      </aside>

      <section>
        {detailQuery.isLoading && <div className="text-swiss-gray-500">Loading narrative...</div>}
        {detailQuery.error && (
          <ErrorState
            title="Narrative Error"
            message={detailQuery.error instanceof Error ? detailQuery.error.message : 'Failed to load narrative'}
            onRetry={() => void detailQuery.refetch()}
          />
        )}
        {!detailQuery.isLoading && !detailQuery.error && detail && (
          <article className="border border-swiss-gray-200 bg-swiss-white px-6 py-6">
            <h2 className="text-[22px] font-semibold tracking-tight">{detail.title}</h2>
            <p className="mt-1 text-[12px] font-swiss-mono text-swiss-gray-400">{detail.date}</p>

            {detail.content.trim() ? (
              <div className="mt-6 space-y-4">
                {renderMarkdown(detail.content).map((section, index) => (
                  <pre
                    key={`${detail.date}-${index}`}
                    className="whitespace-pre-wrap break-words text-[13px] leading-6 text-swiss-black font-swiss-sans"
                  >
                    {section}
                  </pre>
                ))}
              </div>
            ) : (
              <div className="mt-6 text-[13px] text-swiss-gray-500">No narrative for this date yet.</div>
            )}
          </article>
        )}
      </section>
    </div>
  );
}
